import React from 'react';
import { Link } from 'react-router-dom';
import { Check, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { PRICING } from '@/mock/mockData';

const FAQ = [
  { q: 'Do you offer a free trial?', a: 'Yes — every tier includes a 14-day free trial. No credit card required. Full feature access, migration assistance from a real human.' },
  { q: 'Is trust accounting really OMARA & NSW Law Society aligned?', a: 'Yes. We enforce the zero-negative-ledger invariant on every posting, run three-way reconciliation with a hard UI lockout on variance, and generate audit-ready reports in NSW Law Society + OMARA formats.' },
  { q: 'What about AI hallucinations on legal drafting?', a: 'Every AI output requires human review before it hits a form or a client. We use retrieval-augmented generation grounded in DHA, OMARA and Legend sources. AI is advisory, never dispositive.' },
  { q: 'Where is my data hosted?', a: '100% Australia — AWS Sydney (ap-southeast-2). Nothing leaves the country at rest. ISO 27001:2022 controls, TLS 1.3, AES-256.' },
  { q: 'Can I migrate from Migration Manager?', a: 'Yes. We support one-click import of matters, contacts, and time entries via CSV. White-glove migration is included on Growth and above.' },
];

export default function PricingPage() {
  return (
    <>
      <section className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-6 py-20">
          <div className="mx-auto max-w-3xl text-center">
            <Badge variant="secondary" className="bg-[#2ECC9A]/10 text-[#2ECC9A] border-[#2ECC9A]/30">
              Practice-tier pricing
            </Badge>
            <h1 className="mt-4 font-serif text-5xl font-bold leading-tight tracking-tight text-[#1A3A5C]">
              Priced so the solo RMA isn&rsquo;t taxed for being small.
            </h1>
            <p className="mt-4 text-lg text-slate-600">
              14-day free trial on every tier. Cancel anytime. AUD, ex GST.
            </p>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-16">
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {PRICING.map((p) => (
            <Card
              key={p.tier}
              className={`relative flex flex-col border-slate-200 ${
                p.popular ? 'border-[#2ECC9A] shadow-xl ring-1 ring-[#2ECC9A]' : ''
              }`}
            >
              {p.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-[#2ECC9A] px-3 py-1 text-xs font-semibold text-[#1A3A5C]">
                  Most popular
                </div>
              )}
              <CardContent className="flex flex-1 flex-col p-6">
                <div className="text-sm font-semibold uppercase tracking-widest text-[#2ECC9A]">{p.tier}</div>
                <div className="mt-1 text-xs text-slate-500">{p.target}</div>
                <div className="mt-5 flex items-baseline gap-1">
                  {p.price !== null ? (
                    <>
                      <span className="font-serif text-4xl font-bold text-[#1A3A5C]">A${p.price}</span>
                      <span className="text-sm text-slate-500">{p.unit}</span>
                    </>
                  ) : (
                    <span className="font-serif text-3xl font-bold text-[#1A3A5C]">Custom</span>
                  )}
                </div>
                <div className="mt-4 space-y-1 text-xs text-slate-500">
                  <div>Active matters: <span className="font-medium text-[#1A3A5C]">{p.matters}</span></div>
                  <div>Users included: <span className="font-medium text-[#1A3A5C]">{p.users}</span></div>
                  <div>AI calls / month: <span className="font-medium text-[#1A3A5C]">{p.ai}</span></div>
                </div>
                <ul className="mt-6 flex-1 space-y-2 text-sm text-slate-700">
                  {p.features.map((f) => (
                    <li key={f} className="flex gap-2">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#2ECC9A]" /> {f}
                    </li>
                  ))}
                </ul>
                <Link to="/demo" className="mt-6">
                  <Button
                    className={`w-full h-11 ${
                      p.popular
                        ? 'bg-[#2ECC9A] text-[#1A3A5C] hover:bg-[#2ECC9A]/90'
                        : 'bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90'
                    }`}
                  >
                    {p.price === null ? 'Talk to sales' : 'Start free trial'}
                  </Button>
                </Link>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Add-ons */}
        <div className="mt-16 rounded-xl border border-slate-200 bg-white p-8">
          <div className="grid gap-8 md:grid-cols-4">
            <div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-[#2ECC9A]" />
                <div className="font-semibold text-[#1A3A5C]">Add-ons</div>
              </div>
              <div className="mt-2 text-sm text-slate-600">Pay only for what you use beyond your plan.</div>
            </div>
            {[
              { l: 'Extra AI calls', v: 'A$0.10 each' },
              { l: 'Extra storage', v: 'A$0.02 / GB / mo above 50GB' },
              { l: 'SMS (Twilio pass-through)', v: 'cost + 10%' },
            ].map((a) => (
              <div key={a.l} className="rounded-md bg-[#F4F7FA] p-4">
                <div className="text-xs uppercase tracking-widest text-slate-500">{a.l}</div>
                <div className="mt-1 font-semibold text-[#1A3A5C]">{a.v}</div>
              </div>
            ))}
          </div>
        </div>

        {/* FAQ */}
        <div className="mt-16">
          <h2 className="font-serif text-3xl font-bold tracking-tight text-[#1A3A5C]">Frequently asked</h2>
          <div className="mt-6 space-y-3">
            {FAQ.map((f) => (
              <details key={f.q} className="group rounded-lg border border-slate-200 bg-white p-5">
                <summary className="flex cursor-pointer list-none items-center justify-between font-medium text-[#1A3A5C]">
                  {f.q}
                  <span className="text-[#2ECC9A] transition-transform group-open:rotate-45">+</span>
                </summary>
                <div className="mt-3 text-sm text-slate-600">{f.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
